import React, {useState, useEffect, useMemo, useCallback} from "react";
import {Collapse, Select} from "antd";
import {OrderFormPanelKeys} from "../../consts.jsx";
import {expandIconController} from "../../MarketplaceHelper.jsx";
import {FormInputV2} from "../../../components/form/index.jsx";
import {ProductUpgradeOptionsAmountType} from "../../../consts.js";
import {SimpleUploader, StyledButton} from "../../../components/index.jsx";
import {TrashIcon} from "../../../icons/index.jsx";
import {openWindow} from "../../../http/WindowOpener.jsx";
import {createUrlWithoutTransformations} from "../../../utils/cloudinaryUtils.jsx";
import {OrderFormPanelHeader} from "../../OrderForm.jsx";
import shortid from "shortid";
import {TIME_ERROR} from "../../MarketplaceUtils.jsx";
import {OrderFormErrorLabel} from "../../OrderFormErrorLabel.jsx";
import {FoodRequestsIdToName} from "../../ProductsFilterModal/consts.jsx";

const {Panel} = Collapse;

const makeRow = () => ({
    id: shortid.generate(),
    isCustom: true,
    type: ProductUpgradeOptionsAmountType.byUnit,
    name: "",
    amount: "",
    price: ""
});

const isComplete = r => r.name && !isNaN(r.amount) && r.amount > 0 && !isNaN(r.price) && r.price > 0;

export const PackageCustomUpgradeInfoPanel = ({
    formValues,
    getPanelClassName,
    activeKey,
    onActiveKeyChange,
    onFormValueChange,
    onRemoveFile,
    onAddFile,
    readOnly = true
}) => {
    const initRows = useMemo(
        () => (formValues?.upgradeOptions?.options || []).filter(o => o.isCustom),
        [formValues?.upgradeOptions?.options]
    );

    const foodRequestOptions = Object.entries(FoodRequestsIdToName).map(([value, label]) => ({value, label}));

    const [rows, setRows] = useState(initRows.length ? initRows.map(r => ({...r, id: makeRow().id})) : [makeRow()]);
    const files = useMemo(() => (formValues?.upgradeOptions?.files || []).filter(f => f.isCustom), [formValues]);

    const completedRows = useMemo(() => rows.filter(isComplete), [rows]);

    const fullOptions = useMemo(
        () =>
            completedRows.map(({name, amount, price}) => ({
                isCustom: true,
                type: ProductUpgradeOptionsAmountType.byUnit,
                name,
                amount: Number(amount),
                price: Number(price)
            })),
        [completedRows]
    );

    useEffect(() => {
        onFormValueChange("upgradeOptions.options", [
            ...(formValues?.upgradeOptions?.options?.filter(o => !o.isCustom) ?? []),
            ...fullOptions
        ]);
    }, [fullOptions]);

    const updateOptionField = useCallback(
        (id, field, value) =>
            setRows(optionRow => optionRow.map(option => (option.id === id ? {...option, [field]: value} : option))),
        []
    );
    const addRowOption = useCallback(() => {
        const lastRow = rows[rows.length - 1];
        if (isComplete(lastRow)) {
            setRows(optionRow => [...optionRow, makeRow()]);
        }
    }, [rows]);

    const removeRowOption = id =>
        setRows(optionRow => (optionRow.length > 1 ? optionRow.filter(option => option.id !== id) : [makeRow()]));

    const optionNames = useMemo(() => completedRows.map(option => option.name), [completedRows]);

    return (
        <Collapse
            ghost
            accordion
            className={getPanelClassName(OrderFormPanelKeys.UpgradeOptionsCustom)}
            expandIconPosition="left"
            activeKey={activeKey}
            onChange={onActiveKeyChange}
            expandIcon={({isActive}) => expandIconController(isActive)}>
            <Panel
                className="package-custom-upgrade-info-panel"
                key={OrderFormPanelKeys.UpgradeOptionsCustom}
                header={
                    <OrderFormPanelHeader
                        title="✍️ פירוט הרכב ההזמנה העלאת קובץ"
                        isActive={!!optionNames.length || !!files.length}
                        subTitle={
                            <>
                                {optionNames.join(", ") ||
                                    "באפשרותכם לתאר את הרכב החבילה, לפרט מה יהיה כלול בהזמנה ולהעלות קובץ רלוונטי"}
                            </>
                        }
                    />
                }>
                {rows.map(option => (
                    <div
                        key={option.id}
                        className="booking-upgrade-options active-option"
                        style={{display: "flex", flexDirection: "row", flexWrap: "unset"}}>
                        <FormInputV2
                            disabled={readOnly}
                            placeholder="שם"
                            value={option.name}
                            onChange={e => updateOptionField(option.id, "name", e.target.value)}
                        />
                        <FormInputV2
                            disabled={readOnly}
                            type="number"
                            placeholder="כמות"
                            value={option.amount}
                            onChange={e => updateOptionField(option.id, "amount", e.target.value)}
                        />
                        <FormInputV2
                            disabled={readOnly}
                            type="number"
                            placeholder="מחיר לפריט"
                            value={option.price}
                            onChange={e => updateOptionField(option.id, "price", e.target.value)}
                        />
                        <Select
                            disabled={readOnly}
                            mode="multiple"
                            value={option.foodRequests || []}
                            placeholder="התאמות תזונתיות"
                            style={{borderRadius: "5px"}}
                            options={foodRequestOptions}
                            onChange={value => updateOptionField(option.id, "foodRequests", value)}
                        />
                        {!readOnly ? (
                            <TrashIcon
                                className="remove-option-btn"
                                style={{fill: "red", cursor: "pointer", width: "60px", height: "40px"}}
                                onClick={() => removeRowOption(option.id)}
                            />
                        ) : null}
                    </div>
                ))}

                {!readOnly ? (
                    <StyledButton onClick={addRowOption} style={{marginTop: 12, borderRadius: "unset"}}>
                        הוסף פריט
                    </StyledButton>
                ) : null}

                <div className="order-form-upgrade-options-file-line" style={{marginTop: 20}}>
                    <span>טעינת קבצים רלוונטים</span>
                    <div className="order-form-upgrade-options-files-container">
                        {files.map(customFile => (
                            <span key={customFile.fileId} className="order-form-upgrade-options-file">
                                <span onClick={() => openWindow(createUrlWithoutTransformations(customFile.fileUrl))}>
                                    {customFile.fileName}
                                </span>
                                {!readOnly ? (
                                    <TrashIcon
                                        style={{fill: "red"}}
                                        onClick={() => onRemoveFile(customFile.fileId, true)}
                                    />
                                ) : null}
                            </span>
                        ))}
                    </div>
                    {!readOnly && (
                        <SimpleUploader
                            accept="*"
                            onFinish={file =>
                                onAddFile({
                                    ...file,
                                    isCustom: true
                                })
                            }>
                            העלה קובץ
                        </SimpleUploader>
                    )}
                </div>
            </Panel>
        </Collapse>
    );
};
